import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Activity, 
  Check,
  ArrowLeft,
  Scale,
  Ruler,
  Zap,
  Moon,
  Wine,
  Target
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';

const CheckInPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    waist_cm: '',
    weight_kg: '',
    energy_level: 3,
    sleep_quality: 3,
    alcohol_nights: 0,
    rule_adherence: {},
    obstacles: ''
  });

  useEffect(() => {
    if (!user?.is_paid) {
      navigate('/pricing');
      return;
    }
    fetchPlan();
  }, [user]);

  const fetchPlan = async () => {
    try {
      const response = await api.get('/plan/current');
      setPlan(response.data);
      // Initialize rule adherence
      const adherence = {};
      response.data.rules?.forEach(rule => {
        adherence[rule.id] = false;
      });
      setFormData(prev => ({ ...prev, rule_adherence: adherence }));
    } catch (error) {
      toast.error('Please generate a plan first');
      navigate('/plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.waist_cm) {
      toast.error('Waist measurement is required');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        waist_cm: parseFloat(formData.waist_cm),
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        energy_level: formData.energy_level,
        sleep_quality: formData.sleep_quality,
        alcohol_nights: formData.alcohol_nights,
        rule_adherence: formData.rule_adherence,
        obstacles: formData.obstacles || null
      };

      const response = await api.post('/checkin/submit', submitData);
      toast.success(`Check-in recorded! Adherence: ${response.data.adherence_percentage}%`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRuleToggle = (ruleId, checked) => {
    setFormData(prev => ({
      ...prev,
      rule_adherence: {
        ...prev.rule_adherence,
        [ruleId]: checked
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-[#2F3E30] animate-pulse mx-auto mb-4" />
          <p className="text-[#525252]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#525252] hover:text-[#1A1C19]">
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>WEEKLY CHECK-IN</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              HOW WAS YOUR WEEK?
            </h1>
            <p className="text-[#525252]">Takes less than 2 minutes</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Measurements */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                <Ruler className="w-5 h-5 text-[#2F3E30]" />
                MEASUREMENTS
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Waist (cm) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.waist_cm}
                    onChange={(e) => setFormData(prev => ({ ...prev, waist_cm: e.target.value }))}
                    placeholder="96"
                    className="mt-2 bg-[#E8E4DC] border-[#D1D1CD] h-12"
                    data-testid="checkin-waist-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm">Weight (kg) - optional</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData(prev => ({ ...prev, weight_kg: e.target.value }))}
                    placeholder="85"
                    className="mt-2 bg-[#E8E4DC] border-[#D1D1CD] h-12"
                    data-testid="checkin-weight-input"
                  />
                </div>
              </div>
            </div>

            {/* Energy & Sleep */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                <Zap className="w-5 h-5 text-[#F59E0B]" />
                ENERGY & SLEEP
              </h3>
              <div className="space-y-6">
                <div>
                  <Label className="text-sm mb-4 block">Energy level this week: {formData.energy_level}/5</Label>
                  <Slider
                    value={[formData.energy_level]}
                    onValueChange={([value]) => setFormData(prev => ({ ...prev, energy_level: value }))}
                    max={5}
                    min={1}
                    step={1}
                    className="py-4"
                    data-testid="checkin-energy-slider"
                  />
                  <div className="flex justify-between text-xs text-[#525252]">
                    <span>Exhausted</span>
                    <span>Energetic</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm mb-4 block">Sleep quality this week: {formData.sleep_quality}/5</Label>
                  <Slider
                    value={[formData.sleep_quality]}
                    onValueChange={([value]) => setFormData(prev => ({ ...prev, sleep_quality: value }))}
                    max={5}
                    min={1}
                    step={1}
                    className="py-4"
                    data-testid="checkin-sleep-slider"
                  />
                  <div className="flex justify-between text-xs text-[#525252]">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alcohol */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                <Wine className="w-5 h-5 text-[#EF4444]" />
                ALCOHOL
              </h3>
              <div>
                <Label className="text-sm mb-4 block">Nights with alcohol this week: {formData.alcohol_nights}</Label>
                <Slider
                  value={[formData.alcohol_nights]}
                  onValueChange={([value]) => setFormData(prev => ({ ...prev, alcohol_nights: value }))}
                  max={7}
                  min={0}
                  step={1}
                  className="py-4"
                  data-testid="checkin-alcohol-slider"
                />
                <div className="flex justify-between text-xs text-[#525252]">
                  <span>0 nights</span>
                  <span>7 nights</span>
                </div>
              </div>
            </div>

            {/* Rule Adherence */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                <Target className="w-5 h-5 text-[#10B981]" />
                RULE ADHERENCE
              </h3>
              <p className="text-sm text-[#525252] mb-4">Did you follow these rules this week?</p>
              <div className="space-y-3">
                {plan?.rules?.map((rule) => (
                  <div 
                    key={rule.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-[#E8E4DC] border border-[#D1D1CD]"
                  >
                    <Checkbox
                      id={`rule-${rule.id}`}
                      checked={formData.rule_adherence[rule.id] || false}
                      onCheckedChange={(checked) => handleRuleToggle(rule.id, checked)}
                      data-testid={`rule-checkbox-${rule.id}`}
                    />
                    <Label htmlFor={`rule-${rule.id}`} className="cursor-pointer flex-1">
                      <span className="font-medium">{rule.name}</span>
                      <p className="text-sm text-[#525252]">{rule.description}</p>
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Obstacles */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <h3 className="font-bold mb-4" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                WHAT GOT IN THE WAY?
              </h3>
              <Textarea
                value={formData.obstacles}
                onChange={(e) => setFormData(prev => ({ ...prev, obstacles: e.target.value }))}
                placeholder="Travel, work stress, social events... (optional)"
                className="bg-[#E8E4DC] border-[#D1D1CD] min-h-[100px]"
                data-testid="checkin-obstacles-input"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-14 bg-[#2F3E30] hover:bg-[#3A4D3B] text-white text-lg glow-primary"
              data-testid="checkin-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Check-in'}
              {!submitting && <Check className="w-5 h-5 ml-2" />}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CheckInPage;
